import { Currency } from '@/config';
import { tryCatch } from '@/helpers/trycatch';
import {
  Account,
  AccountType,
  BudgetRepeatType,
  calendarDaySchema,
  calendarSummarySchema,
  connectionSchema,
  familyInviteSchema,
  familySchema,
  ProviderAccounts,
  ReceiptAnalyzeResponse,
  RecurringTransaction,
  SaltedgeCategories,
  selectBudgetSchema,
  selectBudgetDetailSchema,
  Transaction,
  transactionSchema,
  transactionSelectSchema,
  userSchema,
  futureTransactionSchema,
  FutureTransaction,
  ApiErrorCode,
} from '@/types';
import { format, isValid, parseISO } from 'date-fns';
import * as z from 'zod';
import { parseApiResponse, parsePaginatedResponse, parseApiError } from '@/helpers/api-response';
import analytics from './analytics';
import { errorTracker } from './errorTracking';

// Scoped fetch wrapper to automatically handle Request and Flow IDs
const originalFetch = globalThis.fetch;
const responseRequestIds = new WeakMap<Response, string>();

const requestRoute = (input: RequestInfo | URL): string => {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  return input.url;
};

const getLocalUserId = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  const clerk = (window as Window & { Clerk?: { user?: { id?: string } | null } }).Clerk;
  return clerk?.user?.id;
};

/** Request id sent on the call, or the id echoed by the response when present. */
export const getRequestId = (response: Response): string | undefined =>
  responseRequestIds.get(response);

const logApiObservation = (
  level: 'info' | 'error',
  route: string,
  requestId: string,
  flowId: string | null | undefined,
  timestamp: string,
  status?: number,
) => {
  const userId = getLocalUserId();
  const message = `[API] ${route} | Request ID: ${requestId} | Flow ID: ${flowId ?? 'none'} | User: ${userId ?? 'none'} | At: ${timestamp}${status != null ? ` | Status: ${status}` : ''}`;
  if (level === 'error') {
    console.error(message);
    return;
  }
  console.info(message);
};

const fetch = async (
  input: RequestInfo | URL,
  init?: RequestInit,
  flowId?: string | null,
): Promise<Response> => {
  const headers = new Headers(init?.headers);
  const sentRequestId = crypto.randomUUID();
  headers.set('X-Request-Id', sentRequestId);

  if (flowId) {
    headers.set('X-Flow-Id', flowId);
  }

  const route = requestRoute(input);
  const timestamp = new Date().toISOString();

  try {
    const response = await originalFetch(input, {
      ...init,
      headers,
    });

    const responseRequestId =
      response.headers.get('x-request-id') || response.headers.get('X-Request-Id');
    const requestId = responseRequestId || sentRequestId;
    responseRequestIds.set(response, requestId);

    logApiObservation(
      response.ok ? 'info' : 'error',
      route,
      requestId,
      flowId,
      timestamp,
      response.status,
    );

    return response;
  } catch (error) {
    logApiObservation('error', route, sentRequestId, flowId, timestamp);
    console.error(`[API Error] fetch: ${route}`, error);
    throw error;
  }
};

const handleApiResponse = async (
  response: Response,
): Promise<{ success: boolean; errorCode?: ApiErrorCode }> => {
  if (response.ok) {
    return { success: true };
  }
  try {
    const body = await response.json();
    const parsedError = parseApiError(body);
    if (parsedError?.code) {
      return { success: false, errorCode: parsedError.code as ApiErrorCode };
    }
  } catch {}

  if (response.status === 401) {
    return { success: false, errorCode: 'AUTH_UNAUTHORIZED' };
  }
  if (response.status === 403) {
    return { success: false, errorCode: 'AUTH_FORBIDDEN' };
  }

  return { success: false, errorCode: 'generic' };
};

const isoDateString = z
  .string()
  .refine((value) => isValid(parseISO(value)), { message: 'Invalid ISO date string' });

const overviewWidgetSchema = z.object({
  totalIncome: z.number(),
  spent: z.number(),
  upcomingExpenses: z.number(),
  openCreditCardBalance: z.number(),
  currency: z.string(),
  nextIncome: z
    .object({
      date: isoDateString.nullable(),
      amount: z.number(),
      currency: z.string(),
      description: z.string(),
      merchant: z.string(),
    })
    .nullable(),
  incomes: z
    .object({
      amount: z.number(),
      currency: z.string(),
      description: z.string().nullable(),
      merchant: z.string().nullable(),
    })
    .array(),
  cycle: z.object({
    startDate: isoDateString,
    endDate: isoDateString,
  }),
});

const fetchAllTransactions = async (
  getToken: () => Promise<string | null>,
  from?: Date,
  to?: Date,
  category?: string,
  merchant?: string,
  amountFrom?: string,
  amountTo?: string,
) => {
  const token = await getToken();
  if (!token) {
    return [];
  }

  const allTransactions: Transaction[] = [];
  let nextPage: string | null = null;
  const limit = 50; // Fetch in chunks of 50

  try {
    // Keep fetching until there are no more pages
    do {
      const result = await fetchTransactions(
        getToken,
        from,
        to,
        category, // category
        merchant, // merchant
        amountFrom, // amountFrom
        amountTo, // amountTo
        nextPage || undefined, // Convert null to undefined
        limit,
      );

      // Add the transactions from this page to our collection
      allTransactions.push(...result.data);

      // Update nextPage for the next iteration
      nextPage = result.next_page;
    } while (nextPage !== null);

    return allTransactions;
  } catch (error) {
    console.error('Error fetching all transactions:', error);
    return allTransactions; // Return whatever we've collected so far
  }
};

const fetchTransactions = async (
  getToken: () => Promise<string | null>,
  from?: Date,
  to?: Date,
  category?: string,
  merchant?: string,
  amountFrom?: string,
  amountTo?: string,
  next_page?: string,
  limit?: number,
  accountId?: number,
  excludeAccountId?: number,
) => {
  try {
    const token = await getToken();
    const baseUrl = `${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/transactions`;

    // Create URLSearchParams object to handle parameters
    const params = new URLSearchParams();

    // Add parameters conditionally
    if (from) params.append('from', from.toISOString());
    if (to) params.append('to', to.toISOString());
    if (category) params.append('cat', category);
    if (next_page) params.append('next_page', next_page.toString());
    if (merchant) params.append('mer', merchant);
    if (amountFrom) params.append('agte', amountFrom.toString());
    if (amountTo) params.append('alte', amountTo.toString());
    if (accountId !== undefined) params.append('acc', accountId.toString());
    if (excludeAccountId !== undefined) params.append('exclude_acc', excludeAccountId.toString());
    params.append('lim', limit ? limit.toString() : '20');
    // Build the URL with parameters
    const url = params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      throw new Error('Network response was not ok');
    } else {
      const parsed = await response.json();
      return parsePaginatedResponse<Transaction>(parsed);
    }
  } catch (error) {
    console.log('error:', error);
    return { data: [], next_page: null };
  }
};

const markCreditcardPayment = async (
  token: string,
  transactionUUID: string,
  creditCardAccountId: number,
): Promise<{ success: boolean; errorCode?: ApiErrorCode }> => {
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/transaction/mark-as-credit-card-payment/${transactionUUID}`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          creditCardAccountId: creditCardAccountId,
        }),
      },
    );
    const result = await handleApiResponse(response);
    if (result.success) {
      analytics.event('credit_card_payment_marked');
    }
    return result;
  } catch (error) {
    console.error('markCreditcardPayment ~ error:', error);
    return { success: false, errorCode: 'generic' };
  }
};

const fetchTransaction = async (getToken: () => Promise<string | null>, uuid: string) => {
  const token = await getToken();
  if (!token) return null;
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/transaction?uuid=${uuid}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  if (!response.ok) {
    throw new Error(`fetchTransaction ~ non-ok response: ${response.status}`);
  }
  const parsed = await response.json();
  return transactionSchema.parse(parseApiResponse(parsed));
};

const fetchCategories = async (getToken: () => Promise<string | null>) => {
  const token = await getToken();
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/categories`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    if (!response.ok) {
      throw new Error('Network response was not ok');
    } else {
      const parsed = await response.json();
      return parseApiResponse<SaltedgeCategories>(parsed) as SaltedgeCategories;
    }
  } catch (error) {
    console.error('Fetch categories failed:', error);
    return null;
  }
};

const fetchUserCategories = async (getToken: () => Promise<string | null>) => {
  const token = await getToken();
  if (!token) {
    return [];
  }
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/user-categories`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  if (response.status === 200) {
    const body = await response.json();
    return z.string().array().parse(parseApiResponse(body));
  }
  return [];
};

const fetchUserMerchants = async (getToken: () => Promise<string | null>) => {
  const token = await getToken();
  if (!token) {
    return [];
  }
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/user-merchants`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  if (response.status === 200) {
    const body = await response.json();
    return z.string().array().parse(parseApiResponse(body));
  }
  return [];
};

const fetchAccounts = async (getToken: () => Promise<string | null>) => {
  const token = await getToken();
  if (!token) {
    const error = new Error('Missing auth token');
    (error as { status?: number }).status = 401;
    throw error;
  }
  const response = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/accounts`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = new Error('Failed to fetch accounts');
    (error as { status?: number }).status = response.status;
    throw error;
  }

  const parsed = await response.json();
  return parseApiResponse(parsed) as {
    providerAccounts: ProviderAccounts[];
    cashAccounts: Account[];
    manualAccounts: Account[];
  };
};

const fetchAccountSnapshots = async (getToken: () => Promise<string | null>, startDate: Date) => {
  const token = await getToken();
  const from = format(startDate, 'yyyy-MM-dd');
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/accounts/snapshots?from=${from}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    if (!response.ok) {
      throw new Error('Network response was not ok');
    } else {
      const parsed = await response.json();
      const snapshotSchema = z.array(
        z.object({
          account_id: z.string(),
          date: z.string(),
          balance: z.string(),
          currency: z.string(),
          //account_type: z.enum(['saltedge', 'manual']),
          nature: z.enum([
            'account',
            'bonus',
            'card',
            'checking',
            'credit',
            'credit_card',
            'debit_card',
            'ewallet',
            'insurance',
            'investment',
            'loan',
            'mortgage',
            'savings',
            'cash_docomondo',
          ]),
          name: z.string(),
        }),
      );
      return snapshotSchema.parse(parseApiResponse(parsed));
    }
  } catch (error) {
    console.error('fetchAccountSnapshots ~ error:', error);
    return [];
  }
};

const initAccountSnapshots = async (getToken: () => Promise<string | null>) => {
  const token = await getToken();
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/accounts/snapshots/init`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    return true;
  } catch (error) {
    console.error('Init account snapshots failed:', error);
    return false;
  }
};

const refreshLead = async (getToken: () => Promise<string | null>) => {
  const token = await getToken();
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/lead/refresh`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );
  console.log(response.status);
};

// const createLead = async () => {
//   const result = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/saltedge/leads'` {
//     method: 'POST',
//     headers: {
//       'Content-Type': 'application/json',
//     },
//   });

//   const json = await result.json();
// };

const createTransaction = async (
  getToken: () => Promise<string | null>,
  accountId: number | null,
  merchant: string,
  amount: number,
  currency: string,
  date: Date,
  category: string | null,
  description?: string | null,
  amountDefaultCurrency?: number | null,
  defaultCurrency?: string | null,
  uuid?: string,
  hasReceipt?: boolean,
): Promise<{ success: boolean; errorCode?: ApiErrorCode }> => {
  const token = await getToken();
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/transaction`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          accountId,
          merchant,
          amount,
          currency,
          date: date.toISOString(),
          category,
          description,
          amountDefaultCurrency,
          defaultCurrency,
          uuid,
          hasReceipt,
        }),
      },
    );
    const result = await handleApiResponse(response);
    if (result.success) {
      analytics.event('transaction_created');
    }
    return result;
  } catch (error) {
    console.error('Create transaction failed:', error);
    return { success: false, errorCode: 'generic' };
  }
};

const updateTransaction = async (
  getToken: () => Promise<string | null>,
  transactionUUID: string,
  data: {
    category?: string | null;
    date?: Date;
    amount?: number;
    currency?: string;
    merchant?: string | null;
    description?: string | null;
    amountDefaultCurrency?: number | null;
    defaultCurrency?: string | null;
  },
): Promise<{ success: boolean; errorCode?: ApiErrorCode }> => {
  const token = await getToken();
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/transaction`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          transactionUUID,
          ...data,
          date: data.date ? data.date.toISOString() : undefined,
        }),
      },
    );
    const result = await handleApiResponse(response);
    if (result.success) {
      analytics.event('transaction_updated');
    }
    return result;
  } catch (error) {
    console.error('Update transaction failed:', error);
    return { success: false, errorCode: 'generic' };
  }
};

const splitTransaction = async (
  getToken: () => Promise<string | null>,
  transactionUUID: string,
  splits: {
    uuid?: string;
    amount: number;
    amountDefaultCurrency?: number | null;
    currency?: string;
    category: string;
    merchant?: string | null;

    description?: string | null;
  }[],
): Promise<{ success: boolean; errorCode?: ApiErrorCode }> => {
  const token = await getToken();
  if (!token) return { success: false, errorCode: 'AUTH_UNAUTHORIZED' };
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/transaction/${transactionUUID}/split`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ splits }),
      },
    );
    const result = await handleApiResponse(response);
    if (result.success) {
      analytics.event('transaction_split');
    }
    return result;
  } catch (error) {
    console.error('Transaction split failed:', error);
    return { success: false, errorCode: 'generic' };
  }
};

const unsplitTransaction = async (
  getToken: () => Promise<string | null>,
  transactionUUID: string,
): Promise<{ success: boolean; errorCode?: ApiErrorCode }> => {
  const token = await getToken();
  if (!token) return { success: false, errorCode: 'AUTH_UNAUTHORIZED' };
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/transaction/${transactionUUID}/unsplit`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      },
    );
    const result = await handleApiResponse(response);
    if (result.success) {
      analytics.event('transaction_unsplit_transaction');
    }
    return result;
  } catch (error) {
    console.error('Transaction unsplit failed:', error);
    return { success: false, errorCode: 'generic' };
  }
};

const getSavings = async (getToken: () => Promise<string | null>, year: string) => {
  const token = await getToken();
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/savings?year=${year}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    if (!response.ok) {
      return false;
    }
    const parsed = await response.json();
    const rawData = parseApiResponse(parsed, { preserveResults: true }) ?? parsed;
    if (Array.isArray(rawData)) {
      return { results: rawData.length, data: rawData };
    }
    return rawData as {
      results: number;
      data: { month: number; value: number; accumulated: number }[];
    };
  } catch (error) {
    console.error('Get savings failed:', error);
    return false;
  }
};

const addSaltedgeCustomerId = async (
  getToken: () => Promise<string | null>,
  customerId: string,
) => {
  const token = await getToken();
  if (!token) {
    return null;
  }
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/customerId`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ customerId }),
      },
    );
    return response.status === 201;
  } catch (error) {
    console.error('Add Saltedge customer ID failed:', error);
    return null;
  }
};

const fetchUser = async (getToken: () => Promise<string | null>) => {
  const token = await getToken();
  if (!token) {
    const error = new Error('Missing authentication token');
    (error as { status?: number }).status = 401;
    throw error;
  }

  const response = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/api/v1/user/budgetUser`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = new Error('Failed to fetch user');
    (error as { status?: number }).status = response.status;
    throw error;
  }

  const parsed = await response.json();
  const rawData = parseApiResponse(parsed) ?? parsed;
  const user = userSchema.parse(rawData);

  return {
    ...user,
    customerId: user?.customerId ? BigInt(user?.customerId) : null,
  };
};

const fetchPlainUser = async (getToken: () => Promise<string | null>) => {
  const token = await getToken();
  if (!token) {
    return null;
  }

  try {
    const response = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/api/v1/user`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.status === 200) {
      const responseJSON = await response.json();
      const rawData = parseApiResponse(responseJSON) ?? responseJSON;
      const user = z
        .object({
          id: z.number(),
          uuid: z.string(),
          email: z.string(),
          name: z.string(),
          token: z.string(),
        })
        .parse(rawData);

      return user;
    }
  } catch (error) {
    console.error('fetchPlainUser ~ error:', error);
  }
  return null;
};

const fetchFamily = async (getToken: () => Promise<string | null>) => {
  const token = await getToken();
  const response = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/api/v1/family`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = new Error('Failed to fetch family');
    (error as { status?: number }).status = response.status;
    throw error;
  }

  const text = await response.text();
  if (!text) return null;

  try {
    const parsed = JSON.parse(text);
    const rawData = parseApiResponse(parsed);
    if (!rawData) return null;
    const family = familySchema.parse(rawData);
    return family;
  } catch (error) {
    console.error('Failed to parse JSON response:', error);
    return null;
  }
};

const fetchFamilyInvites = async (getToken: () => Promise<string | null>) => {
  const token = await getToken();
  try {
    const response = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/api/v1/family/invites`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.status === 200) {
      const text = await response.text();
      if (!text || text === '[]') return [];
      try {
        const parsed = JSON.parse(text);
        const rawData = parseApiResponse(parsed);
        if (!rawData || !Array.isArray(rawData)) return [];
        const family = familyInviteSchema.array().parse(rawData);
        return family;
      } catch (error) {
        console.error('Failed to parse JSON response:', error);
        return [];
      }
    }
    throw new Error('Failed to fetch family invites');
  } catch (error) {
    console.error('fetchFamilyInvites ~ error:', error);
    return [];
  }
};

const respondToFamilyInvite = async (
  token: string,
  inviteId: number,
  accept: boolean,
): Promise<{ success: boolean; errorCode?: ApiErrorCode }> => {
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/api/v1/family/invitations/${inviteId}`,
      {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ accept }),
      },
    );

    return await handleApiResponse(response);
  } catch (error) {
    console.error('respondToFamilyInvite ~ error:', error);
    return { success: false, errorCode: 'generic' };
  }
};

const removeFamilyMember = async (
  getToken: () => Promise<string | null>,
  memberId: number,
): Promise<{ success: boolean; errorCode?: ApiErrorCode }> => {
  const token = await getToken();
  if (!token) {
    return { success: false, errorCode: 'AUTH_UNAUTHORIZED' };
  }
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/api/v1/family/members/${memberId}`,
      {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (response.status === 200) {
      analytics.event('family_member_removed');
      return { success: true };
    }
    return handleApiResponse(response);
  } catch (error) {
    console.error('removeFamilyMember ~ error:', error);
    return { success: false, errorCode: 'generic' };
  }
};

const fetchConnections = async (getToken: () => Promise<string | null>) => {
  const token = await getToken();
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/connections`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (response.status === 200) {
      const parsed = await response.json();
      const connections = connectionSchema.array().parse(parseApiResponse(parsed));
      return connections;
    }
    throw new Error('Failed to fetch connections');
  } catch (error) {
    console.error('fetchConnections ~ error:', error);
    throw new Error('Failed to fetch connections');
  }
};

const fetchGoal = async (getToken: () => Promise<string | null>) => {
  const token = await getToken();
  try {
    const response = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/goal`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.status === 200) {
      const parsed = await response.json();
      const goal = z.number().optional().parse(parseApiResponse(parsed));
      return goal;
    }
    throw new Error('Failed to fetch goal');
  } catch (error) {
    console.error('fetchGoal ~ error:', error);
    throw new Error('Failed to fetch goal');
  }
};

const setGoal = async (
  getToken: () => Promise<string | null>,
  value: number,
): Promise<{ success: boolean; errorCode?: ApiErrorCode }> => {
  const token = await getToken();
  try {
    const response = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/goal`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ goal: value }),
    });

    const result = await handleApiResponse(response);
    if (result.success) {
      analytics.event('goal_updated');
    }
    return result;
  } catch (error) {
    console.error('setGoal ~ error:', error);
    return { success: false, errorCode: 'generic' };
  }
};

const fetchBudgets = async (getToken: () => Promise<string | null>) => {
  const token = await getToken();
  try {
    const response = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/budgets`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.status === 200) {
      const parsed = await response.json();
      const budgets = selectBudgetSchema.array().parse(parseApiResponse(parsed));
      return budgets;
    }
    throw new Error('Failed to fetch budgets');
  } catch (error) {
    console.error('fetchbudgets ~ error:', error);
    throw new Error('Failed to fetch budgets');
  }
};

const fetchBudgetDetails = async (getToken: () => Promise<string | null>) => {
  const token = await getToken();
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/budgets/details`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (response.status === 200) {
      const parsed = await response.json();
      const budgets = selectBudgetDetailSchema.array().parse(parseApiResponse(parsed));
      return budgets;
    }
    throw new Error('Failed to fetch budget details');
  } catch (error) {
    console.error('fetchBudgetDetails ~ error:', error);
    throw new Error('Failed to fetch budget details');
  }
};

const addBudget = async (
  getToken: () => Promise<string | null>,
  category: string,
  value: number,
  repeat: BudgetRepeatType,
): Promise<{ success: boolean; errorCode?: ApiErrorCode }> => {
  const token = await getToken();
  try {
    const response = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/budgets`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        category,
        value,
        repeat,
      }),
    });
    const result = await handleApiResponse(response);
    if (result.success) {
      analytics.event('budget_created');
    }
    return result;
  } catch (error) {
    console.error('addBudget ~ error:', error);
    return { success: false, errorCode: 'generic' };
  }
};

const deleteBudget = async (
  token: string,
  budgetId: number,
): Promise<{ success: boolean; errorCode?: ApiErrorCode }> => {
  try {
    const response = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/budgets`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id: budgetId }),
    });
    const result = await handleApiResponse(response);
    if (result.success) {
      analytics.event('budget_deleted');
    }
    return result;
  } catch (error) {
    console.error('deleteBudget ~ error:', error);
    return { success: false, errorCode: 'generic' };
  }
};

const inviteFamilyMember = async (
  token: string,
  email: string,
): Promise<{ success: boolean; errorCode?: ApiErrorCode }> => {
  try {
    const response = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/api/v1/family/invite`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    if (response.status === 201) {
      analytics.event('family_member_invited');
      return { success: true };
    }
    return handleApiResponse(response);
  } catch (error) {
    console.error('inviteFamilyMember ~ error:', error);
    return { success: false, errorCode: 'generic' };
  }
};

const leaveFamily = async (
  token: string,
): Promise<{ success: boolean; errorCode?: ApiErrorCode }> => {
  try {
    const response = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/api/v1/family/leave`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.status === 200) {
      return { success: true };
    }
    return handleApiResponse(response);
  } catch (error) {
    console.error('leaveFamily ~ error:', error);
    return { success: false, errorCode: 'generic' };
  }
};

const createSaltedgeConnection = async (token: string, locale: string) => {
  const _lng = locale === 'de' ? 'de' : 'en';
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/se/connections/connect`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          redirect_url: `${process.env.NEXT_PUBLIC_BASE_URL}/${_lng}/accounts`,
          locale: _lng,
        }),
      },
    );

    if (response.status === 201) {
      const parsed = await response.json();
      const payload =
        parseApiResponse<{ connect_url: string }>(parsed) ??
        (parsed as { data?: { connect_url: string } })?.data;
      analytics.event('bank_connection_started');
      return payload?.connect_url ?? null;
    }
    return null;
  } catch (error) {
    console.error('createSaltedgeConnection ~ error:', error);
    return null;
  }
};

const disconnectAccount = async (
  token: string,
  connectionId: string,
): Promise<{ success: boolean; errorCode?: ApiErrorCode }> => {
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/se/connections/${connectionId}`,
      {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    const result = await handleApiResponse(response);
    if (result.success) {
      analytics.event('bank_connection_disconnected');
    }
    return result;
  } catch (error) {
    console.error('disconnectAccount ~ error:', error);
    return { success: false, errorCode: 'generic' };
  }
};

const toggleRecurringTransaction = async (
  token: string,
  transactionUUID: string,
  enabled: boolean,
): Promise<{ success: boolean; errorCode?: ApiErrorCode }> => {
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/recurring-transaction/${transactionUUID}`,
      {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enabled }),
      },
    );
    const result = await handleApiResponse(response);
    if (result.success) {
      analytics.event('recurring_transaction_toggled');
    }
    return result;
  } catch (error) {
    console.error('toggleRecurringTransaction ~ error:', error);
    return { success: false, errorCode: 'generic' };
  }
};

const toggleMainIncome = async (
  token: string,
  transactionUUID: string,
  isMainIncome: boolean,
): Promise<{ success: boolean; errorCode?: ApiErrorCode }> => {
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/recurring-transaction-main-income/${transactionUUID}`,
      {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          isMainIncome: isMainIncome,
        }),
      },
    );
    const result = await handleApiResponse(response);
    if (result.success) {
      analytics.event('main_income_toggled');
    }
    return result;
  } catch (error) {
    console.error('toggleMainIncome ~ error:', error);
    return { success: false, errorCode: 'generic' };
  }
};

const deleteTransaction = async (
  token: string | null,
  transactionUUID: string,
): Promise<{ success: boolean; errorCode?: ApiErrorCode }> => {
  if (!token) {
    return { success: false, errorCode: 'AUTH_UNAUTHORIZED' };
  }
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/transaction/${transactionUUID}`,
      {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    const result = await handleApiResponse(response);
    if (result.success) {
      analytics.event('transaction_deleted');
    }
    return result;
  } catch (error) {
    console.error('deleteTransaction ~ error:', error);
    return { success: false, errorCode: 'generic' };
  }
};

const deleteRecurringTransaction = async (
  token: string,
  transactionUUID: string,
): Promise<{ success: boolean; errorCode?: ApiErrorCode }> => {
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/recurring-transaction/${transactionUUID}`,
      {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    const result = await handleApiResponse(response);
    if (result.success) {
      analytics.event('recurring_transaction_deleted');
    }
    return result;
  } catch (error) {
    console.error('deleteRecurringTransaction ~ error:', error);
    return { success: false, errorCode: 'generic' };
  }
};

const fetchRecurringTransactions = async (getToken: () => Promise<string | null>) => {
  const token = await getToken();
  if (!token) {
    return [];
  }
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/recurring-transactions`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    if (!response.ok) {
      console.error('Failed to fetch recurring transactions:', response.status);
      return [];
    }
    const body = await response.json();
    const list = parseApiResponse<RecurringTransaction[]>(body);
    return Array.isArray(list) ? list : [];
  } catch (error) {
    console.error('fetchRecurringTransactions ~ error:', error);
    return [];
  }
};

const createRecurringTransaction = async (
  token: string,
  transaction: Transaction | FutureTransaction,
  frequency: string,
  executionDay: number | null,
  startDate: string,
  endDate: string | null,
  isMainIncome?: boolean,
): Promise<{ success: boolean; errorCode?: ApiErrorCode }> => {
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/recurring-transaction`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          transactionId: transaction.uuid,
          merchant: (() => {
            if (!transaction.merchant) {
              console.warn(
                'createRecurringTransaction ~ merchant missing for transaction:',
                transaction.uuid,
              );
            }
            return transaction.merchant ?? 'unknown';
          })(),
          accountId: transaction.account_id,
          category: transaction.category,
          amount: transaction.amount,
          currency: transaction.currency,
          frequency: frequency,
          description: transaction.description,
          dayOfMonth: executionDay,
          isMainIncome: isMainIncome,
          startDate: startDate,
          endDate: endDate,
        }),
      },
    );

    const result = await handleApiResponse(response);
    if (result.success) {
      analytics.event('recurring_transaction_created');
    }
    return result;
  } catch (error) {
    console.error('createRecurringTransaction ~ error:', error);
    return { success: false, errorCode: 'generic' };
  }
};

const getFutureTransactions = async (token: string, from: Date, to: Date) => {
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/future-transactions?from=${from?.toISOString()}&to=${to?.toISOString()}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    const json = await response.json();
    return await futureTransactionSchema.array().parse(parseApiResponse(json));
  } catch (error) {
    console.error('getFutureTransactions ~ error:', error);
    return [];
  }
};

const fetchOverviewData = async (getToken: () => Promise<string | null>) => {
  const token = await getToken();
  if (!token) {
    return null;
  }
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/overview-widget-data`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    const json = await response.json();
    const rawData = parseApiResponse(json) ?? json;
    const body = overviewWidgetSchema.parse(rawData);
    return body;
  } catch (error) {
    console.error('fetchOverviewData ~ error:', error);
    return null;
  }
};

const editDefaultCurrency = async (getToken: () => Promise<string | null>, currency: string) => {
  const token = await getToken();
  if (!token) {
    return false;
  }
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/api/v1/user/budget/default-currency`,
      {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ currency }),
      },
    );
    if (response.ok) {
      analytics.event('default_currency_changed');
      return true;
    }
    throw new Error('Failed to edit default currency');
  } catch (error) {
    console.error('editDefaultCurrency ~ error:', error);
    throw error;
  }
};

const fetchExchangeRates = async (
  getToken: () => Promise<string | null>,
  baseCurrency: string | undefined | null,
) => {
  if (!baseCurrency) {
    return [];
  }
  const token = await getToken();
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_BACKEND_URL}/api/v1/exchange-rates/base-currency?base=${baseCurrency}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  if (response.status === 200) {
    const result = await response.json();
    const data = parseApiResponse<{ currency: Currency; rate: number }[]>(result);
    return data ?? (result as { currency: Currency; rate: number }[]);
  }
  console.error('Failed to fetch exchange rates:', response.status);
  return [];
};

const updateAccount = async (
  token: string,
  accountId: string,
  data: { name?: string; balance?: number; balanceUpdateMode?: 'soft' | 'hard' },
): Promise<{ success: boolean; errorCode?: ApiErrorCode }> => {
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/account/${accountId}`,
      {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      },
    );
    const result = await handleApiResponse(response);
    if (result.success) {
      if (data.name !== undefined) {
        analytics.event('account_renamed');
      }
    }
    return result;
  } catch (error) {
    console.error('updateAccount ~ error:', error);
    return { success: false, errorCode: 'generic' };
  }
};

const renameAccount = async (
  token: string,
  accountId: string,
  newName: string,
): Promise<{ success: boolean; errorCode?: ApiErrorCode }> => {
  return updateAccount(token, accountId, { name: newName });
};

const createAccount = async (
  token: string,
  balance: number,
  currency: string,
  account_type: AccountType,
  nature: 'cash_docomondo' | 'account' | 'credit_card',
  name: string,
): Promise<{ success: boolean; data?: number; errorCode?: ApiErrorCode }> => {
  try {
    const body: {
      balance: number;
      currency: string;
      account_type: AccountType;
      nature: 'cash_docomondo' | 'account' | 'credit_card';
      name?: string;
    } = {
      balance,
      currency,
      account_type,
      nature,
    };

    if (account_type === 'manual') {
      body.name = name;
    }

    const response = await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/account`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    if (response.status === 201) {
      const resBody = await response.json();
      const rawData = parseApiResponse(resBody) ?? resBody;
      const id =
        typeof rawData === 'number'
          ? rawData
          : ((rawData as { id?: number })?.id ?? (rawData as { data?: number })?.data);
      analytics.event('account_created');
      return { success: true, data: id as number };
    }
    return handleApiResponse(response);
  } catch (error) {
    console.error('createAccount ~ error:', error);
    return { success: false, errorCode: 'generic' };
  }
};

const fetchCategoryData = async (
  getToken: () => Promise<string | null>,
  type: string,
  from: Date,
  to: Date,
) => {
  if (!from || !to) {
    return [];
  }
  const token = await getToken();
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/analytics/category-data?type=${type}&from=${from.toISOString()}&to=${to.toISOString()}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  if (!response.ok) {
    console.error('fetchCategoryData ~ http error', response.status);
    return [];
  }
  const body = await response.json();
  const schema = z.object({ data: z.array(z.object({ category: z.string(), sum: z.string() })) });
  const rawData = parseApiResponse(body) ?? body;
  const [error, parsed] = tryCatch(() =>
    schema.parse(Array.isArray(rawData) ? { data: rawData } : rawData),
  );
  if (error) {
    console.error('fetchCategoryData ~ error:', error);
    return [];
  }
  return parsed.data;
};

const fetchMerchantData = async (
  getToken: () => Promise<string | null>,
  period: string,
  merchant: string | undefined,
  from: Date,
  to: Date,
) => {
  const emptyResponse = {
    group_type: 'DAY',
    data: [],
  };

  if (!from || !to || !period || !merchant) {
    return emptyResponse;
  }
  const token = await getToken();
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/analytics/merchant-data?period=${period}&merchant=${encodeURIComponent(merchant)}&from=${from.toISOString()}&to=${to.toISOString()}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  if (!response.ok) {
    console.error('fetchMerchantData ~ http error', response.status);
    return emptyResponse;
  }
  const body = await response.json();
  const schema = z.object({
    group_type: z.enum(['DAY', 'WEEK', 'MONTH']),
    data: z.array(z.object({ group_key: z.string(), income: z.string(), spendings: z.string() })),
  });
  const rawData = parseApiResponse(body) ?? body;
  const [error, parsed] = tryCatch(() => schema.parse(rawData));
  if (error) {
    console.error('fetchMerchantData ~ error:', error);
    return {
      group_type: 'DAY',
      data: [],
    };
  }
  return parsed;
};

const fetchIncomeExpenses = async (
  getToken: () => Promise<string | null>,
  from: Date,
  to: Date,
) => {
  const token = await getToken();
  if (!token) {
    return [];
  }
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/analytics/income-expenses?from=${from.toISOString()}&to=${to.toISOString()}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  if (!response.ok) {
    console.error('fetchIncomeExpenses ~ http error', response.status);
    return [];
  }
  const body = await response.json();
  const schema = z.array(z.object({ date: z.string(), income: z.string(), spendings: z.string() }));
  const rawData = parseApiResponse(body) ?? body;
  const [error, parsed] = tryCatch(() => schema.parse(rawData));
  if (error) {
    console.error('fetchIncomeExpenses ~ error:', error);
    return [];
  }
  return parsed;
};

async function updateUserAppInfo(apiToken: string, browser: string) {
  // track last active of user with current device
  const version_code = 'web';
  const platform = 'web';

  if (!apiToken) {
    return;
  }

  const data = {
    app: 'BUDGET',
    platform,
    device_id: browser,
    platform_version: version_code,
  };
  const [error] = await tryCatch(
    fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/api/v1/user/activity`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + apiToken,
      },
      body: JSON.stringify(data),
    }),
  );

  if (error) {
    console.error('updateUserAppInfo ~ error:', error);
  }
}

const uploadReceiptToS3 = async (
  getToken: () => Promise<string | null>,
  transactionUuid: string,
  file: File,
  flowId?: string | null,
): Promise<{ success: boolean; errorCode?: ApiErrorCode }> => {
  try {
    const token = await getToken();
    if (!token) {
      console.error('uploadReceiptToS3 ~ user not authenticated');
      errorTracker.captureException(new Error('uploadReceiptToS3 ~ user not authenticated'), {
        flowId: flowId ?? undefined,
        extra: { errorCode: 'AUTH_UNAUTHORIZED' },
      });
      return { success: false, errorCode: 'AUTH_UNAUTHORIZED' };
    }

    // 1. Get presigned post URL
    const presignedResponse = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/api/v1/s3/presigned-post`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          key: `budget/${transactionUuid}/receipt.pdf`,
          type: file.type,
          size: file.size,
        }),
      },
      flowId,
    );

    if (!presignedResponse.ok) {
      console.error('uploadReceiptToS3 ~ failed to get presigned URL:', presignedResponse.status);
      errorTracker.captureException(
        new Error(`uploadReceiptToS3 ~ failed to get presigned URL: ${presignedResponse.status}`),
        {
          flowId: flowId ?? undefined,
          requestId: getRequestId(presignedResponse),
          extra: { errorCode: 'generic' },
        },
      );
      return { success: false, errorCode: 'generic' };
    }

    const presignedJson = await presignedResponse.json();
    const presignedData =
      parseApiResponse<{ url: string; fields: Record<string, string> }>(presignedJson) ??
      (presignedJson as { url?: string; fields?: Record<string, string> });

    if (!presignedData?.url || !presignedData?.fields) {
      console.error('uploadReceiptToS3 ~ invalid presigned response: missing required fields', {
        hasUrl: Boolean(presignedData?.url),
        hasFields: Boolean(presignedData?.fields),
      });
      return { success: false, errorCode: 'generic' };
    }

    // 2. Upload file directly to S3
    const formData = new FormData();

    // IMPORTANT: Add all fields from presigned response FIRST
    Object.entries(presignedData.fields).forEach(([key, value]) => {
      formData.append(key, value);
    });

    // IMPORTANT: Add file LAST
    formData.append('file', file);

    // 3. POST to S3 (no flowId needed for direct S3 upload)
    const uploadResponse = await fetch(presignedData.url, {
      method: 'POST',
      body: formData,
    });

    if (!uploadResponse.ok) {
      console.error('uploadReceiptToS3 ~ failed to upload to S3:', uploadResponse.status);
      errorTracker.captureException(
        new Error(`uploadReceiptToS3 ~ failed to upload to S3: ${uploadResponse.status}`),
        {
          flowId: flowId ?? undefined,
          requestId: getRequestId(uploadResponse),
          extra: { errorCode: 'generic' },
        },
      );
      return { success: false, errorCode: 'generic' };
    }
    analytics.event('receipt_uploaded', {
      active_flow_id: flowId || '',
    });
    return { success: true };
  } catch (error) {
    console.error('uploadReceiptToS3 ~ error:', error);
    errorTracker.captureException(error as Error, {
      flowId: flowId ?? undefined,
      tags: { message: 'Error uploading receipt' },
    });
    return { success: false, errorCode: 'generic' };
  }
};

const analyzeReceipt = async (
  getToken: () => Promise<string | null>,
  file: File,
  transactionUuid?: string,
  skipOcr?: boolean,
  flowId?: string | null,
): Promise<{
  success: boolean;
  data?: ReceiptAnalyzeResponse;
  errorCode?: ApiErrorCode;
  flowId: string | null;
}> => {
  try {
    const token = await getToken();
    if (!token) {
      return { success: false, errorCode: 'AUTH_UNAUTHORIZED', flowId: flowId || null };
    }

    const formData = new FormData();
    formData.append('file', file);
    if (transactionUuid) {
      formData.append('transactionUuid', transactionUuid);
    }

    const url = `${process.env.NEXT_PUBLIC_BACKEND_URL}/api/v1/receipts/analyze${skipOcr ? '?skipOcr=true' : ''}`;

    const response = await fetch(
      url,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          // Content-Type is set automatically by browser with boundary for FormData
        },
        body: formData,
      },
      flowId,
    );

    if (response.ok) {
      const json = await response.json();
      const data = (parseApiResponse<ReceiptAnalyzeResponse>(json) ??
        json) as ReceiptAnalyzeResponse;
      analytics.event('receipt_analyzed', {
        active_flow_id: flowId || '',
      });
      return { success: true, data, flowId: flowId || null };
    }
    const errorResult = await handleApiResponse(response);
    errorTracker.captureException(new Error(`Receipt analysis failed: ${response.status}`), {
      flowId: flowId ?? undefined,
      requestId: getRequestId(response),
      extra: { errorCode: errorResult.errorCode },
    });
    return { success: false, errorCode: errorResult.errorCode, flowId: flowId || null };
  } catch (error) {
    console.error('analyzeReceipt ~ error:', error);
    errorTracker.captureException(error as Error, {
      flowId: flowId ?? undefined,
      tags: { message: 'Error analyzing receipt' },
    });
    return { success: false, errorCode: 'generic', flowId: flowId || null };
  }
};

const getReceiptUrl = async (
  getToken: () => Promise<string | null>,
  transactionUuid: string,
  flowId?: string,
) => {
  const token = await getToken();
  if (!token) return null;

  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/api/v1/s3/generate-temporary-url`,
      {
        method: 'POST',
        body: JSON.stringify({
          key: `budget`,
          cloudId: transactionUuid,
          name: 'receipt.pdf',
          isReceipt: false,
        }),
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      },
      flowId,
    );

    if (response.status === 201) {
      const json = await response.json();
      const rawData =
        parseApiResponse<{ temporaryUrl: string }>(json) ?? (json as { temporaryUrl?: string });
      const temporaryUrl = typeof rawData === 'string' ? rawData : rawData?.temporaryUrl;
      return temporaryUrl as string;
    }
    console.error('getReceiptUrl ~ unexpected status:', response.status);
    return null;
  } catch (error) {
    console.error('getReceiptUrl ~ error:', error);
    return null;
  }
};

const deleteBudgetReceipt = async (
  getToken: () => Promise<string | null>,
  transactionUuid: string,
) => {
  const token = await getToken();
  if (!token) {
    return false;
  }

  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/api/v1/receipts/delete-receipt/${transactionUuid}`,
      {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      console.error('deleteBudgetReceipt ~ error status:', response.status);
      return false;
    }

    return true;
  } catch (error) {
    console.error('deleteBudgetReceipt ~ error:', error);
    return false;
  }
};

const fetchCsvJobs = async (
  getToken: () => Promise<string | null>,
): Promise<import('@/types').CsvJobSummary[]> => {
  const token = await getToken();
  if (!token) return [];
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/api/v1/receipts/csv-jobs`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (!response.ok) return [];
    const data = await response.json();
    const rawData = parseApiResponse<import('@/types').CsvJobSummary[]>(data) ?? data;
    return (rawData as import('@/types').CsvJobSummary[]) || [];
  } catch {
    return [];
  }
};

const uploadCsv = async (
  getToken: () => Promise<string | null>,
  file: File,
  accountId?: number,
): Promise<{ jobId: string; status: 'pending' }> => {
  const token = await getToken();
  if (!token) {
    throw new Error('Missing authentication token');
  }
  const formData = new FormData();
  formData.append('file', file);
  if (accountId !== undefined) {
    formData.append('accountId', String(accountId));
  }
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_BACKEND_URL}/api/v1/receipts/analyze-csv`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    },
  );

  const bodyText = await response.text();
  let data;
  try {
    data = JSON.parse(bodyText);
  } catch (e) {
    console.error('JSON parse failed for upload:', e);
    if (!response.ok) {
      throw new Error(`Upload failed (status: ${response.status}): ${bodyText}`);
    }
    throw new Error(
      `Upload failed (status: ${response.status}) - Invalid JSON response: ${bodyText}`,
    );
  }

  if (!response.ok) {
    throw new Error(`Upload failed (status: ${response.status}): ${JSON.stringify(data)}`);
  }

  return data;
};

const getCsvJobStatus = async (
  getToken: () => Promise<string | null>,
  jobId: string,
): Promise<import('@/types').CsvJobResponse> => {
  const token = await getToken();
  if (!token) {
    throw new Error('Missing authentication token');
  }
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_BACKEND_URL}/api/v1/receipts/csv-status/${jobId}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    },
  );

  const bodyText = await response.text();
  let data;
  try {
    data = JSON.parse(bodyText);
  } catch (e) {
    console.error('JSON parse failed for csv-status:', e);
    if (!response.ok) {
      throw new Error(`Status check failed (status: ${response.status}): ${bodyText}`);
    }
    throw new Error(
      `Status check failed (status: ${response.status}) - Invalid JSON response: ${bodyText}`,
    );
  }

  if (!response.ok) {
    throw new Error(`Status check failed (status: ${response.status}): ${JSON.stringify(data)}`);
  }

  return data;
};

const cleanupCsvJob = async (
  getToken: () => Promise<string | null>,
  jobId: string,
): Promise<void> => {
  const token = await getToken();
  if (!token) {
    return;
  }
  try {
    await fetch(`${process.env.NEXT_PUBLIC_BACKEND_URL}/api/v1/receipts/csv-status/${jobId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (error) {
    console.error('cleanupCsvJob ~ error:', error);
  }
};

const fetchCalendarMonth = async (getToken: () => Promise<string | null>, month: Date) => {
  const token = await getToken();
  if (!token) return null;
  const monthStr = format(month, 'yyyy-MM');
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/calendar?month=${monthStr}`,
    {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  if (!response.ok) {
    throw new Error(`fetchCalendarMonth ~ non-ok response: ${response.status}`);
  }
  const parsed = await response.json();
  return z.object({ days: calendarDaySchema.array() }).parse(parseApiResponse(parsed));
};

const fetchCalendarDay = async (getToken: () => Promise<string | null>, date: Date) => {
  const token = await getToken();
  if (!token) return null;
  const dateStr = format(date, 'yyyy-MM-dd');
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/calendar/day?date=${dateStr}`,
    {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  if (!response.ok) {
    console.error(`fetchCalendarDay ~ non-ok response: ${response.status}`);
    throw new Error(`fetchCalendarDay ~ non-ok response: ${response.status}`);
  }
  const parsed = await response.json();
  const safeParsed = transactionSelectSchema
    .omit({ id: true })
    .array()
    .safeParse(parseApiResponse(parsed));
  if (!safeParsed.success) {
    console.error(`fetchCalendarDay ~ invalid transactions: ${JSON.stringify(safeParsed.error)}`);
    throw new Error(`fetchCalendarDay ~ invalid transactions: ${JSON.stringify(safeParsed.error)}`);
  }
  return safeParsed.data;
};

const fetchCalendarSummary = async (getToken: () => Promise<string | null>) => {
  const token = await getToken();
  if (!token) return null;
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/calendar/summary`,
    {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  if (!response.ok) {
    throw new Error(`fetchCalendarSummary ~ non-ok response: ${response.status}`);
  }
  const parsed = await response.json();
  return calendarSummarySchema.parse(parseApiResponse(parsed));
};

export type BatchCreateResult =
  | { ok: true }
  | { ok: false; error?: any }
  | { partial: true; details: any };

const batchCreateTransactions = async (
  getToken: () => Promise<string | null>,
  transactions: import('@/types').CsvTransactionCreate[],
  updateAccountBalance?: boolean,
): Promise<BatchCreateResult> => {
  const token = await getToken();
  if (!token) {
    return { ok: false, error: 'Missing token' };
  }
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/budget/api/v1/transactions`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ transactions, updateAccountBalance }),
      },
    );
    if (!response.ok || response.status === 207) {
      const rawBody = await response.text();
      let parsedBody;
      try {
        parsedBody = JSON.parse(rawBody);
      } catch {
        parsedBody = rawBody || null;
      }

      console.error(
        `batchCreateTransactions ~ error/partial (status: ${response.status}):`,
        parsedBody,
      );

      if (response.status === 207) {
        return { partial: true, details: { status: response.status, body: parsedBody } };
      }
      return { ok: false, error: { status: response.status, body: parsedBody } };
    }
    analytics.event('csv_imported');
    return { ok: true };
  } catch (error) {
    console.error('batchCreateTransactions ~ error:', error);
    return { ok: false, error };
  }
};

const authApiInterface = {
  //createLead,
  fetchUser,
  fetchPlainUser,
  fetchFamily,
  fetchFamilyInvites,
  respondToFamilyInvite,
  fetchConnections,
  fetchTransactions,
  fetchAllTransactions,
  markCreditcardPayment,
  fetchTransaction,
  fetchCategories,
  fetchUserCategories,
  fetchUserMerchants,
  fetchAccounts,
  fetchGoal,
  setGoal,
  fetchBudgets,
  fetchBudgetDetails,
  addBudget,
  deleteBudget,
  createTransaction,
  updateTransaction,
  splitTransaction,
  unsplitTransaction,
  getSavings,
  addSaltedgeCustomerId,
  fetchAccountSnapshots,
  initAccountSnapshots,
  refreshLead,
  inviteFamilyMember,
  leaveFamily,
  removeFamilyMember,
  createSaltedgeConnection,
  disconnectAccount,
  createRecurringTransaction,
  getFutureTransactions,
  fetchRecurringTransactions,
  toggleRecurringTransaction,
  toggleMainIncome,
  deleteRecurringTransaction,
  deleteTransaction,
  fetchOverviewData,
  editDefaultCurrency,
  fetchExchangeRates,
  updateAccount,
  renameAccount,
  createAccount,
  fetchCategoryData,
  fetchMerchantData,
  fetchIncomeExpenses,
  updateUserAppInfo,
  uploadReceiptToS3,
  analyzeReceipt,
  deleteBudgetReceipt,
  getReceiptUrl,
  fetchCsvJobs,
  uploadCsv,
  getCsvJobStatus,
  cleanupCsvJob,
  batchCreateTransactions,
  fetchCalendarMonth,
  fetchCalendarDay,
  fetchCalendarSummary,
};

export default authApiInterface;
